"use client";
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Image from 'next/image';

export default function NavBar() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { href: '/features', label: 'Features' },
    { href: '/tools', label: 'Tools' },
    { href: '/pricing', label: 'Pricing' },
    { href: '/pro-services', label: 'Pro Services' },
    { href: '/blog', label: 'Blog' },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full">
      <div className={`transition-all duration-300 ${
        scrolled
          ? 'mx-auto mt-2 w-[95vw] max-w-5xl bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl shadow-lg py-3 px-2 sm:px-6'
          : 'w-full bg-white border-b border-gray-200 py-3 px-2 sm:px-6'
      }`}>
        {/* Mobile Hamburger */}
        <div className="flex items-center justify-between sm:hidden w-full">
          <Link href="/" className="flex items-center gap-2">
            <Image src="/images/logo.png" alt="Superpower Resume Logo" width={80} height={80} className="rounded-xl" />
          </Link>
          <button
            className="p-2 focus:outline-none"
            aria-label="Open navigation menu"
            onClick={() => setOpen(o => !o)}
          >
            <span className="block w-6 h-0.5 bg-gray-800 mb-1 rounded transition-all" style={{ transform: open ? 'rotate(45deg) translateY(7px)' : 'none' }}></span>
            <span className={`block w-6 h-0.5 bg-gray-800 mb-1 rounded transition-all ${open ? 'opacity-0' : ''}`}></span>
            <span className="block w-6 h-0.5 bg-gray-800 rounded transition-all" style={{ transform: open ? 'rotate(-45deg) translateY(-7px)' : 'none' }}></span>
          </button>
        </div>
        {/* Mobile Dropdown */}
        {open && (
          <div className="sm:hidden absolute left-0 top-full mt-2 w-full bg-white rounded-2xl shadow-lg border border-gray-200 flex flex-col py-2 z-50 animate-fade-in">
            {links.map(link => {
              const isActive = pathname === link.href || (link.href !== '/' && pathname.startsWith(link.href));
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={
                    (isActive
                      ? 'bg-gradient-to-r from-blue-700 to-blue-600 text-white '
                      : 'text-gray-800 hover:text-blue-600 hover:bg-blue-50 ') +
                    'font-semibold px-4 py-2 rounded-lg transition-colors mb-1'
                  }
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => setOpen(false)}
                >
                  {link.label}
                </Link>
              );
            })}
            <a
              href="https://app.superpowerresume.com/sign-in"
              className="text-gray-800 hover:text-blue-600 font-semibold px-4 py-2 rounded-lg transition-colors mb-1"
              onClick={() => setOpen(false)}
            >
              Sign In
            </a>
            <a
              href="https://app.superpowerresume.com/sign-up"
              className="text-white bg-blue-600 hover:bg-blue-700 font-semibold transition-colors px-4 py-2 rounded-lg shadow min-w-[110px] text-center"
              onClick={() => setOpen(false)}
            >
              Try Free
            </a>
          </div>
        )}
        {/* Desktop Nav */}
        <div className="hidden sm:flex items-center justify-center gap-x-4 lg:gap-x-6 w-full">
          <Link href="/" className="flex items-center gap-2 mr-6">
            <Image src="/images/logo.png" alt="Superpower Resume Logo" width={80} height={80} className="rounded-xl" />
          </Link>
          {links.map(link => {
            const isActive = pathname === link.href || (link.href !== '/' && pathname.startsWith(link.href));
            return (
              <Link
                key={link.href}
                href={link.href}
                className={
                  isActive
                    ? 'bg-gradient-to-r from-blue-700 to-blue-600 text-white font-semibold text-sm px-3 py-1.5 rounded-lg shadow transition-colors whitespace-nowrap'
                    : 'relative text-gray-700 font-medium text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors hover:text-blue-600 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-0.5 after:w-0 after:bg-blue-600 after:rounded-full after:transition-all after:duration-200 hover:after:w-3/4'
                }
                aria-current={isActive ? 'page' : undefined}
              >
                {link.label}
              </Link>
            );
          })}
          <a
            href="https://app.superpowerresume.com/sign-in"
            className="relative text-gray-700 font-medium text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors hover:text-blue-600"
          >
            Sign In
          </a>
          <a
            href="https://app.superpowerresume.com/sign-up"
            className="text-white bg-blue-600 hover:bg-blue-700 font-semibold text-sm transition-all duration-200 px-5 py-2 rounded-lg shadow hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105 min-w-[100px] text-center whitespace-nowrap"
          >
            Try Free
          </a>
        </div>
      </div>
    </nav>
  );
}
